import React, { useState } from "react";

/**
 * Controlled `<input type="number">` that applies every valid keystroke live
 * but keeps its own draft string while the user is editing.
 *
 * A plain controlled number input fed straight from the store breaks in two
 * ways: clearing it has to commit *something* (so the box snaps to "0"), and
 * React only rewrites a number input's DOM value when `node.value != value`
 * (loose) — a typed "045" loosely equals the stored 45, so "045" stays on
 * screen. Holding the draft locally means an empty field commits nothing and
 * stays empty, leading zeros are dropped as they are typed, and blur hands the
 * display back to the stored value.
 *
 * `onChange` receives a Number, never "". Every other prop is passed through
 * to the `<input>`.
 */
export default function LiveNumberInput({ value, onChange, onBlur, disabled, ...rest }) {
  // null = not editing; show whatever the store holds.
  const [draft, setDraft] = useState(null);

  const stored = value === undefined || value === null ? "" : String(value);
  const shown = disabled ? "" : draft !== null ? draft : stored;

  return (
    <input
      type="number"
      {...rest}
      disabled={disabled}
      value={shown}
      onChange={(e) => {
        const raw = e.target.value.replace(/^(-?)0+(?=\d)/, "$1");
        setDraft(raw);
        if (raw === "") return;
        const n = Number(raw);
        if (Number.isFinite(n)) onChange(n);
      }}
      onBlur={(e) => {
        setDraft(null);
        if (onBlur) onBlur(e);
      }}
    />
  );
}
